import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Avatar, 
  Grid, 
  Paper, 
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  InputAdornment,
  Tooltip,
  LinearProgress,
  styled
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import ErrorIcon from '@mui/icons-material/Error';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { t } from '../../utils/textUtils';

// Estilos personalizados
const CircleAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: `3px solid ${theme.palette.primary.light}`,
  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  position: 'relative'
}));

const AvatarOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.4)',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  opacity: 0,
  transition: 'opacity 0.3s',
  '&:hover': {
    opacity: 1
  }
}));

const PasswordStrengthBar = styled(LinearProgress)(({ value, theme }) => ({
  height: 5,
  borderRadius: 5,
  marginTop: 5,
  marginBottom: 10,
  backgroundColor: theme.palette.grey[300],
  '& .MuiLinearProgress-bar': {
    backgroundColor: 
      value < 30 ? theme.palette.error.main :
      value < 60 ? theme.palette.warning.main :
      theme.palette.success.main
  }
}));

const ProfileSettings = () => {
  const { user, updateUserContext } = useAuth();  // Add updateUserContext here
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  
  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    fullName: '',
    phone: '',
    profilePicture: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Estado para validación en tiempo real
  const [validation, setValidation] = useState({
    username: { valid: true, message: '' },
    email: { valid: true, message: '' },
    phone: { valid: true, message: '' }
  });

  // Fetch user profile data when component mounts or user changes
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user || !user.id) {
        console.log('User data not available yet, waiting...');
        return;
      }

      setLoading(true);
      try {
        console.log('Fetching user profile for ID:', user.id);
        const response = await api.get(`/users/${user.id}`);
        const userData = response.data;
        console.log('User data received:', userData);
        
        // Ensure we have values for all fields to display
        setProfileData({
          username: userData.username || user.username || '',
          email: userData.email || user.email || '',
          fullName: userData.fullName || user.fullName || '',
          phone: userData.phone || '',
          profilePicture: userData.profilePicture || ''
        });
        
        console.log('Profile data set successfully');
      } catch (error) {
        console.error('Error fetching user profile:', error.response || error);
        setAlert({
          open: true,
          message: t('GAGAL MEMUAT PROFIL PENGGUNA'),
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [user]);
  
  // Validar email en tiempo real
  useEffect(() => {
    if (profileData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setValidation(prev => ({
        ...prev,
        email: {
          valid: emailRegex.test(profileData.email),
          message: emailRegex.test(profileData.email) ? '' : 'Format email tidak valid'
        }
      }));
    }
  }, [profileData.email]);
  
  // Calcular fuerza de la contraseña
  useEffect(() => {
    if (passwordData.newPassword) {
      let strength = 0;
      // Longitud mínima
      if (passwordData.newPassword.length >= 8) strength += 20;
      // Contiene números
      if (/\d/.test(passwordData.newPassword)) strength += 20;
      // Contiene minúsculas
      if (/[a-z]/.test(passwordData.newPassword)) strength += 20;
      // Contiene mayúsculas
      if (/[A-Z]/.test(passwordData.newPassword)) strength += 20;
      // Contiene caracteres especiales
      if (/[^A-Za-z0-9]/.test(passwordData.newPassword)) strength += 20;
      
      setPasswordStrength(strength);
    } else {
      setPasswordStrength(0);
    }
  }, [passwordData.newPassword]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validación en tiempo real
    if (name === 'username' && value) {
      setValidation(prev => ({
        ...prev,
        username: {
          valid: value.length >= 3,
          message: value.length >= 3 ? '' : 'Username minimal 3 karakter'
        }
      }));
    } else if (name === 'phone' && value) {
      // Ganti baris yang mengandung \+ dengan karakter yang benar
      // Misalnya, jika ada kode seperti:
      // const phoneRegex = /^[0-9\+\-\s]+$/;
      // Ubah menjadi:
      const phoneRegex = /^[0-9+\-\s]+$/;
      setValidation(prev => ({
        ...prev,
        phone: {
          valid: phoneRegex.test(value),
          message: phoneRegex.test(value) ? '' : 'Nomor telepon hanya boleh berisi angka, +, - atau spasi'
        }
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.put(`/users/${user.id}`, {
        username: profileData.username,
        email: profileData.email,
        fullName: profileData.fullName,
        phone: profileData.phone
      });
      
      setAlert({
        open: true,
        message: t('PROFIL BERHASIL DIPERBARUI'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      setAlert({
        open: true,
        message: t('GAGAL MEMPERBARUI PROFIL'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setAlert({
        open: true,
        message: t('PASSWORD BARU DAN KONFIRMASI TIDAK COCOK'),
        severity: 'error'
      });
      return;
    }
    
    setSaving(true);
    
    try {
      await api.put(`/users/${user.id}/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setAlert({
        open: true,
        message: t('PASSWORD BERHASIL DIPERBARUI'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error changing password:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message ? error.response.data.message : t('GAGAL MEMPERBARUI PASSWORD'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Define the handleProfilePictureChange function
  const handleProfilePictureChange = (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Upload the file
    handleProfilePictureUpload(file);
  };
  
  // Define handleProfilePictureUpload inside the component
  const handleProfilePictureUpload = async (file) => {
    setSaving(true);
    
    try {
      const formData = new FormData();
      formData.append('profilePicture', file);
      
      // Ensure user ID is available
      const userId = user.id || user.userId;
      if (!userId) {
        throw new Error('User ID is undefined');
      }
      
      const response = await api.post(`/users/${userId}/profile-picture`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update profile data with new profile picture URL
      // El backend devuelve la URL en response.data.profilePicture
      const profilePictureUrl = response.data.profilePicture;
      
      console.log('Profile picture response:', response.data);
      
      setProfileData(prev => ({
        ...prev,
        profilePicture: profilePictureUrl
      }));
      
      // Update user context to reflect changes immediately
      updateUserContext({
        ...user,
        profilePicture: profilePictureUrl
      });
      
      // Limpiar la vista previa después de subir la imagen
      setPreviewImage(null);
      
      setAlert({
        open: true,
        message: t('FOTO PROFIL BERHASIL DIPERBARUI'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setAlert({
        open: true,
        message: t('GAGAL MENGUNGGAH FOTO PROFIL'),
        severity: 'error'
      });
      // Limpiar la vista previa en caso de error
      setPreviewImage(null);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    setSaving(true);
    try {
      // Ensure user ID is available
      const userId = user.id || user.userId;
      if (!userId) {
        throw new Error('User ID is undefined');
      }
      
      await api.delete(`/users/${userId}/profile-picture`);
      
      setProfileData(prev => ({
        ...prev,
        profilePicture: ''
      }));
      
      // Update user context to reflect changes immediately
      updateUserContext({
        ...user,
        profilePicture: ''
      });
      
      setAlert({
        open: true,
        message: t('FOTO PROFIL BERHASIL DIHAPUS'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error removing profile picture:', error);
      setAlert({
        open: true,
        message: t('GAGAL MENGHAPUS FOTO PROFIL'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };
  
  const handleTogglePassword = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleCloseAlert = () => {
    setAlert(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1200, margin: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold', color: 'text.primary' }}>
        {t('PENGATURAN AKUN')}
      </Typography>

      <Grid container spacing={{ xs: 2, md: 4 }}>
        {/* Kolom Kiri: Foto Profil dan Info Dasar */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: '12px' }}>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <CircleAvatar 
                src={previewImage || (profileData.profilePicture ? `${profileData.profilePicture}?t=${new Date().getTime()}` : '')}
              >
                {profileData.fullName ? profileData.fullName.charAt(0).toUpperCase() : user?.username.charAt(0).toUpperCase()}
              </CircleAvatar>
              <AvatarOverlay>
                <Tooltip title={t('Ubah Foto Profil')} placement="top">
                  <IconButton sx={{ color: 'white' }} onClick={() => fileInputRef.current.click()}>
                    <PhotoCameraIcon />
                  </IconButton>
                </Tooltip>
                {profileData.profilePicture && (
                  <Tooltip title={t('Hapus Foto Profil')} placement="top">
                    <IconButton sx={{ color: 'white' }} onClick={handleRemoveProfilePicture}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </AvatarOverlay>
            </Box>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleProfilePictureChange}
              accept="image/jpeg, image/png"
              hidden
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {profileData.fullName || t('Nama Pengguna')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {profileData.email}
            </Typography>
            <Button 
              variant="outlined"
              size="small"
              onClick={() => fileInputRef.current.click()}
              sx={{ mt: 2, borderRadius: '20px' }}
            >
              {t('UNGGAH FOTO')}
            </Button>
            <Typography variant="caption" sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
              {t('JPG atau PNG, maks 2MB')}
            </Typography>
          </Paper>
        </Grid>

        {/* Kolom Kanan: Form Edit */}
        <Grid item xs={12} md={8}>
          {/* Form Informasi Profil */}
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 0 }}>
                {t('INFORMASI PROFIL')}
              </Typography>
              <Tooltip title={t('Data profil yang tersimpan')} placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'info.main' }}>
                  <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="caption">{t('Data Tersimpan')}</Typography>
                </Box>
              </Tooltip>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Box component="form" onSubmit={handleProfileSubmit} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="username"
                    label={t('USERNAME')}
                    value={profileData.username}
                    onChange={handleProfileChange}
                    fullWidth
                    required
                    variant="outlined"
                    error={!validation.username.valid}
                    helperText={validation.username.message || t('Minimal 3 karakter')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ color: 'text.secondary' }}>
                          @
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="fullName"
                    label={t('NAMA LENGKAP')}
                    value={profileData.fullName}
                    onChange={handleProfileChange}
                    fullWidth
                    variant="outlined"
                    placeholder={t('Masukkan nama lengkap Anda')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="email"
                    label={t('EMAIL')}
                    value={profileData.email}
                    fullWidth
                    variant="outlined"
                    disabled
                    sx={{ 
                      '& .MuiInputBase-input.Mui-disabled': { 
                        WebkitTextFillColor: '#666', 
                        opacity: 0.8,
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                    helperText={t('Email tidak dapat diubah')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="phone"
                    label={t('NOMOR TELEPON')}
                    value={profileData.phone}
                    onChange={handleProfileChange}
                    fullWidth
                    variant="outlined"
                    placeholder="+62 xxx xxxx xxxx"
                    error={!validation.phone.valid}
                    helperText={validation.phone.message || t('Format: +62 atau 08xx')}
                    InputProps={{
                      startAdornment: profileData.phone ? null : (
                        <InputAdornment position="start" sx={{ color: 'text.secondary' }}>
                          +62
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    sx={{ mt: 1, borderRadius: '20px', px: 3 }}
                  >
                    {saving ? <CircularProgress size={24} color="inherit" /> : t('SIMPAN PERUBAHAN')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Form Ubah Password */}
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', mb: 0 }}>
                {t('UBAH PASSWORD')}
              </Typography>
              <Tooltip title={t('Pastikan password baru cukup kuat')} placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
                  <Typography variant="caption">{t('Minimal 8 karakter')}</Typography>
                </Box>
              </Tooltip>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Box component="form" onSubmit={handlePasswordSubmit} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    name="currentPassword"
                    label={t('PASSWORD SAAT INI')}
                    type={showPassword.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleTogglePassword('current')} edge="end">
                            {showPassword.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="newPassword"
                    label={t('PASSWORD BARU')}
                    type={showPassword.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleTogglePassword('new')} edge="end">
                            {showPassword.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  {passwordData.newPassword && (
                    <Box sx={{ mt: 1 }}>
                      <PasswordStrengthBar variant="determinate" value={passwordStrength} />
                      <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {passwordStrength < 30 && t('Kekuatan: Lemah')}
                        {passwordStrength >= 30 && passwordStrength < 60 && t('Kekuatan: Sedang')}
                        {passwordStrength >= 60 && t('Kekuatan: Kuat')}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="confirmPassword"
                    label={t('KONFIRMASI PASSWORD BARU')}
                    type={showPassword.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    variant="outlined"
                    error={passwordData.newPassword !== passwordData.confirmPassword && passwordData.confirmPassword !== ''}
                    helperText={passwordData.newPassword !== passwordData.confirmPassword && passwordData.confirmPassword !== '' ? t('Password tidak cocok') : ''}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleTogglePassword('confirm')} edge="end">
                            {showPassword.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword || passwordData.newPassword !== passwordData.confirmPassword}
                    sx={{ mt: 1, borderRadius: '20px', px: 3 }}
                  >
                    {saving ? <CircularProgress size={24} color="inherit" /> : t('UBAH PASSWORD')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%', borderRadius: '8px', boxShadow: 3 }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfileSettings;
